import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "@/components/ui/Button.jsx";
import InputPhoneSADC from "@/components/auth/InputPhoneSADC.jsx";
import InputCountrySADC from "@/components/fields/InputCountrySADC.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { createSponsorPaymentLink } from "@/lib/payments/api.ts";
import {
  clearPendingSponsorPayment,
  savePendingSponsorPayment,
} from "@/lib/payments/sponsorPaymentRecovery.js";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import {
  getMarketByName,
  getMarketPaymentCoverage,
  quoteSponsorPresentment,
  resolveSponsorCurrency,
} from "@/services/marketPaymentService.js";
import { sendPaymentRequestEmail } from "@/services/communicationEmailService.js";

const SPONSOR_PAYMENT_DRAFT_KEY = "afrimed:sponsor-payment-draft:v1";

function MetricCard({ children, className = "" }) {
  return (
    <div
      className={[
        "rounded-[12px] bg-[rgb(var(--clr-surface))] p-4 shadow-[var(--elev-1)]",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function SurfaceCard({ children, className = "" }) {
  return (
    <div
      className={[
        "rounded-[12px] bg-[rgb(var(--clr-surface))] p-4 shadow-[var(--elev-1)]",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function MetricValue({ children }) {
  return <div className="mt-2 text-title-medium text-on-surface sm:text-title-large">{children}</div>;
}

export default function SponsorPaymentLinkPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const auth = useAuth() || {};
  const userId = String(auth?.user?.id || "").trim();
  const flowState =
    location.state && typeof location.state === "object" ? location.state : {};
  const flowSource = String(flowState?.source || "preferences_transactions").trim();
  const flowPrefill =
    flowState?.prefill && typeof flowState.prefill === "object" ? flowState.prefill : {};
  const flowMetadata =
    flowState?.metadata && typeof flowState.metadata === "object" ? flowState.metadata : {};
  const flowReturnTo = String(flowState?.returnTo || "").trim();
  const flowReturnState =
    flowState?.returnState && typeof flowState.returnState === "object"
      ? flowState.returnState
      : null;
  const sellerRole = String(
    flowState?.sellerRole ||
      flowState?.seller_role ||
      (flowSource === "pharmacy_prescription_checkout" ? "pharmacy" : "doctor")
  )
    .trim()
    .toLowerCase() === "pharmacy"
    ? "pharmacy"
    : "doctor";
  const sellerLabel =
    sellerRole === "pharmacy"
      ? t("settings.preferences.transactions.sponsorSellerPharmacy", "pharmacy")
      : t("settings.preferences.transactions.sponsorSellerDoctor", "doctor");
  const sellerDisplayName = String(
    flowState?.sellerName ||
      flowState?.seller_name ||
      flowState?.doctorName ||
      ""
  ).trim();
  const hasHydratedDraftRef = React.useRef(false);
  const coverage = React.useMemo(() => getMarketPaymentCoverage(), []);
  const [copied, setCopied] = React.useState(false);
  const sponsorPresentmentAuto =
    (flowSource === "appointment_checkout" ||
      flowSource === "pharmacy_prescription_checkout") &&
    Boolean(String(flowPrefill.amount || "").trim());
  const basePaymentRef = React.useRef({
    amount: String(flowPrefill.amount || "").trim(),
    currency: String(flowPrefill.currency || "ZAR").trim().toUpperCase() || "ZAR",
  });
  const [linkState, setLinkState] = React.useState({
    loading: false,
    url: "",
    error: "",
    feeAmount: 0,
    doctorNetAmount: 0,
    amountCents: 0,
    currency: "",
    doctorDestinationAttached: true,
    paymentLinkId: "",
  });
  const [emailState, setEmailState] = React.useState({
    sending: false,
    error: "",
    success: "",
  });
  const [fxHint, setFxHint] = React.useState({
    loading: false,
    reference: "",
    rate: 1,
    upliftMultiplier: 1,
    sourceAmount: String(flowPrefill.amount || "").trim(),
    sourceCurrency: String(flowPrefill.currency || "ZAR").trim().toUpperCase() || "ZAR",
  });

  const [form, setForm] = React.useState({
    sponsorName: "",
    relationship: "",
    sponsorCountry: "",
    sponsorPhone: "",
    sponsorDialCode: "+44",
    sponsorEmail: "",
    paymentFor: "",
    amount: "",
    currency: "USD",
  });

  React.useEffect(() => {
    if (!userId) return undefined;
    try {
      const raw = window.localStorage.getItem(`${SPONSOR_PAYMENT_DRAFT_KEY}:${userId}`);
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return undefined;
      setForm((prev) => ({
        ...prev,
        sponsorName: String(parsed.sponsorName || ""),
        relationship: String(parsed.relationship || ""),
        sponsorCountry: String(parsed.sponsorCountry || ""),
        sponsorPhone: String(parsed.sponsorPhone || ""),
        sponsorDialCode: String(parsed.sponsorDialCode || prev.sponsorDialCode || "+44"),
        sponsorEmail: String(parsed.sponsorEmail || ""),
        paymentFor: String(parsed.paymentFor || prev.paymentFor || ""),
        amount: String(parsed.amount || prev.amount || ""),
        currency: String(parsed.currency || prev.currency || "USD"),
      }));
    } catch {
      // Ignore malformed drafts.
    } finally {
      hasHydratedDraftRef.current = true;
    }
    return undefined;
  }, [userId]);

  React.useEffect(() => {
    basePaymentRef.current = {
      amount: String(flowPrefill.amount || "").trim(),
      currency: String(flowPrefill.currency || "ZAR").trim().toUpperCase() || "ZAR",
    };

    setForm((prev) => {
      const currentPaymentFor = String(prev.paymentFor || "").trim();
      const currentAmount = String(prev.amount || "").trim();
      const currentCurrency = String(prev.currency || "").trim();

      return {
        ...prev,
        paymentFor:
          flowSource === "appointment_checkout" || flowSource === "pharmacy_prescription_checkout"
            ? String(flowPrefill.paymentFor || "").trim() || currentPaymentFor
            : currentPaymentFor || String(flowPrefill.paymentFor || "").trim() || "",
        amount:
          flowSource === "appointment_checkout" || flowSource === "pharmacy_prescription_checkout"
            ? String(flowPrefill.amount || "").trim() || currentAmount
            : currentAmount || String(flowPrefill.amount || "").trim() || "",
        currency:
          flowSource === "appointment_checkout" || flowSource === "pharmacy_prescription_checkout"
            ? String(flowPrefill.currency || "").trim() || currentCurrency || "USD"
            : currentCurrency || String(flowPrefill.currency || "").trim() || "USD",
        };
    });
  }, [flowPrefill.amount, flowPrefill.currency, flowPrefill.paymentFor, flowSource]);

  React.useEffect(() => {
    let alive = true;
    if (!sponsorPresentmentAuto) {
      setFxHint((prev) => ({
        ...prev,
        loading: false,
        reference: "",
        rate: 1,
        sourceAmount: String(basePaymentRef.current.amount || "").trim(),
        sourceCurrency:
          String(basePaymentRef.current.currency || "ZAR").trim().toUpperCase() || "ZAR",
      }));
      return undefined;
    }

    async function syncSponsorPresentment() {
      const sourceAmount = String(basePaymentRef.current.amount || "").trim();
      const sourceCurrency =
        String(basePaymentRef.current.currency || "ZAR").trim().toUpperCase() || "ZAR";
      const targetCurrency = resolveSponsorCurrency({
        sponsorCountry: form.sponsorCountry,
        sponsorDialCode: form.sponsorDialCode,
        fallbackCurrency: sourceCurrency,
      });

      setFxHint((prev) => ({
        ...prev,
        loading: true,
        sourceAmount,
        sourceCurrency,
      }));

      const quote = await quoteSponsorPresentment({
        amount: sourceAmount,
        sourceCurrency,
        sponsorCountry: form.sponsorCountry,
        sponsorDialCode: form.sponsorDialCode,
      });

      if (!alive) return;

      setForm((prev) => ({
        ...prev,
        amount: String(quote.amount || sourceAmount),
        currency: String(quote.currency || targetCurrency || sourceCurrency),
      }));
      setFxHint({
        loading: false,
        reference: String(quote.reference || ""),
        rate: Number(quote.rate || 1),
        upliftMultiplier: Number(quote.upliftMultiplier || 1),
        sourceAmount,
        sourceCurrency,
      });
    }

    void syncSponsorPresentment();

    return () => {
      alive = false;
    };
  }, [
    form.sponsorCountry,
    form.sponsorDialCode,
    flowPrefill.amount,
    flowPrefill.currency,
    sponsorPresentmentAuto,
  ]);

  React.useEffect(() => {
    if (!userId || !hasHydratedDraftRef.current) return undefined;
    try {
      window.localStorage.setItem(
        `${SPONSOR_PAYMENT_DRAFT_KEY}:${userId}`,
        JSON.stringify(form)
      );
    } catch {
      // Ignore storage failures.
    }
    return undefined;
  }, [form, userId]);

  React.useEffect(() => {
    if (!copied) return undefined;
    const timeout = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  const patientName = String(
    auth?.profile?.full_name ||
      auth?.profile?.display_name ||
      auth?.user?.user_metadata?.full_name ||
      ""
  ).trim();

  const resolvedCountry = getMarketByName(form.sponsorCountry);
  const sponsorPaymentDefault = t(
    sellerRole === "pharmacy"
      ? "settings.preferences.transactions.sponsorPaymentForPlaceholderPharmacy"
      : "settings.preferences.transactions.sponsorPaymentForPlaceholderDoctor",
    sellerRole === "pharmacy" ? "Prescription fulfilment" : "Telemedicine consultation"
  );
  const sponsorPaymentForLabel = String(form.paymentFor || "").trim() || sponsorPaymentDefault;
  const sponsorPaymentForLine = patientName
    ? t(
        "settings.preferences.transactions.sponsorPaymentForPatient",
        "{service} for {name}",
        { service: sponsorPaymentForLabel, name: patientName }
      )
    : sponsorPaymentForLabel;
  const sponsorAmountLabel = [String(form.amount || "").trim(), String(form.currency || "").trim().toUpperCase()]
    .filter(Boolean)
    .join(" ")
    .trim();
  const serviceAmountLabel = [
    String(fxHint.sourceAmount || basePaymentRef.current.amount || "").trim(),
    String(fxHint.sourceCurrency || basePaymentRef.current.currency || "ZAR").trim().toUpperCase(),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const fxReferenceLabel = String(fxHint.reference || "").trim();
  const fxRateLabel =
    sponsorPresentmentAuto && Number(fxHint.rate || 0) > 0
      ? `1 ${String(fxHint.sourceCurrency || "ZAR").trim().toUpperCase()} = ${Number(
          fxHint.rate,
        ).toFixed(6)} ${String(form.currency || "").trim().toUpperCase()}`
      : "";
  const sponsorBrief = [
    t("settings.preferences.transactions.sponsorEmailGreeting", "Hello {name},", {
      name:
        String(form.sponsorName || "").trim() ||
        t("settings.preferences.transactions.sponsorFallbackName", "there"),
    }),
    "",
    t(
      "settings.preferences.transactions.sponsorEmailIntro",
      "AfriMed™ prepared a secure payment request for this care episode as follows:",
    ),
    "",
    sponsorAmountLabel
      ? `${t("settings.preferences.transactions.sponsorBriefAmount", "Requested amount")}: ${sponsorAmountLabel}`
      : "",
    sponsorPaymentForLabel
      ? `${t("settings.preferences.transactions.sponsorBriefPaymentFor", "Payment for")}: ${sponsorPaymentForLabel}`
      : "",
    patientName
      ? `${t("settings.preferences.transactions.sponsorBriefPatient", "Patient")}: ${patientName}`
      : "",
    form.relationship
      ? `${t("settings.preferences.transactions.sponsorBriefRelationshipToPatient", "Relationship to patient")}: ${form.relationship}`
      : "",
    form.sponsorCountry
      ? `${t("settings.preferences.transactions.sponsorBriefSponsorLocation", "Sponsor location")}: ${form.sponsorCountry}`
      : "",
    serviceAmountLabel
      ? `${t("settings.preferences.transactions.sponsorBriefServiceAmount", "Service amount")}: ${serviceAmountLabel}`
      : "",
    fxReferenceLabel
      ? `${t("settings.preferences.transactions.sponsorBriefFxReference", "FX reference")}: ${[fxReferenceLabel, fxRateLabel].filter(Boolean).join(" • ")}`
      : "",
    linkState.url
      ? `${t("settings.preferences.transactions.sponsorBriefLink", "Secure payment link")}: ${linkState.url}`
      : "",
    "",
    linkState.url
      ? t(
          "settings.preferences.transactions.sponsorEmailClosing",
          "Please use the secure AfriMed™ payment link to complete the payment cleanly.",
        )
      : t(
          "settings.preferences.transactions.sponsorBriefNeedLink",
          "Generate the secure AfriMed™ payment link before sending this request so the sponsor receives a real payment action.",
        ),
  ]
    .filter(Boolean)
    .join("\n");
  const canShare = Boolean(String(form.sponsorName || "").trim());
  const canOpenEmailDraft = Boolean(
    String(form.sponsorEmail || "").trim() && String(linkState.url || "").trim(),
  );
  const canGenerateLink =
    Boolean(String(form.sponsorName || "").trim()) &&
    Boolean(String(form.amount || "").trim()) &&
    Number(String(form.amount || "").replace(/,/g, ".")) > 0;

  function patchForm(nextPatch) {
    setLinkState((prev) => ({
      ...prev,
      url: "",
      error: "",
      feeAmount: 0,
      doctorNetAmount: 0,
      amountCents: 0,
      currency: "",
      doctorDestinationAttached: true,
      paymentLinkId: "",
    }));
    setForm((prev) => ({ ...prev, ...nextPatch }));
  }

  function amountToCents(amountLabel) {
    const normalized = String(amountLabel || "")
      .trim()
      .replace(/\s+/g, "")
      .replace(/,/g, ".");
    const numeric = Number(normalized);
    if (!Number.isFinite(numeric) || numeric <= 0) return 0;
    return Math.round(numeric * 100);
  }

  async function handleCopyBrief() {
    if (!canShare) return;
    try {
      await navigator.clipboard.writeText(sponsorBrief);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  function handleOpenEmail() {
    void handleSendEmail();
  }

  async function handleSendEmail() {
    if (!canOpenEmailDraft || emailState.sending) return;
    setEmailState({ sending: true, error: "", success: "" });
    try {
      const response = await sendPaymentRequestEmail({
        sponsor_email: form.sponsorEmail,
        sponsor_name: form.sponsorName,
        relationship: form.relationship,
        sponsor_country: form.sponsorCountry,
        sponsor_dial_code: form.sponsorDialCode,
        sponsor_phone: form.sponsorPhone,
        payment_for: sponsorPaymentForLine,
        amount: form.amount,
        currency: form.currency,
        service_amount: fxHint.sourceAmount || basePaymentRef.current.amount || "",
        service_currency: fxHint.sourceCurrency || basePaymentRef.current.currency || "ZAR",
        fx_reference: fxHint.reference || "",
        fx_rate: fxHint.rate || "",
        payment_link_url: linkState.url,
        patient_name: patientName,
        seller_role: sellerRole,
        seller_name: sellerDisplayName,
      });
      if (!response?.ok && !response?.skipped) {
        throw new Error(response?.error || "We could not send the payment request right now.");
      }
      if (response?.skipped) {
        throw new Error(
          t(
            "settings.preferences.transactions.sponsorEmailSkipped",
            "This sponsor email is currently unavailable because communication preferences block it.",
          ),
        );
      }
      setEmailState({
        sending: false,
        error: "",
        success: t(
          "settings.preferences.transactions.sponsorEmailSent",
          "Payment request email sent.",
        ),
      });
    } catch (error) {
      setEmailState({
        sending: false,
        error:
          String(error?.message || "").trim() ||
          t(
            "settings.preferences.transactions.sponsorEmailSendError",
            "We could not send the payment request right now.",
          ),
        success: "",
      });
    }
  }

  async function handleGenerateLink() {
    if (!canGenerateLink) return;

    setLinkState((prev) => ({
      ...prev,
      loading: true,
      error: "",
    }));

    try {
      const amountCents = amountToCents(form.amount);
      const origin = String(window.location.origin || "").replace(/\/+$/, "");
      const isPharmacySeller = sellerRole === "pharmacy";
      const sellerUserId = String(
        flowState?.sellerUserId ||
          flowState?.seller_user_id ||
          flowState?.pharmacyOwnerUserId ||
          ""
      ).trim();
      const pharmacyOwnerUserId = String(
        flowState?.pharmacyOwnerUserId ||
          flowState?.sellerUserId ||
          flowMetadata?.pharmacy_owner_user_id ||
          ""
      ).trim();
      const doctorUserId = String(flowState?.doctorUserId || "").trim();
      const doctorName = String(flowState?.doctorName || "").trim();
      const resolvedSuccessUrl =
        String(flowState?.successUrl || "").trim() ||
        `${origin}/settings/patient/preferences/transactions?sponsor_payment=1&session_id={CHECKOUT_SESSION_ID}`;
      const resolvedCancelUrl =
        String(flowState?.cancelUrl || "").trim() ||
        `${origin}/settings/patient/preferences/transactions/prepare-sponsor-payment`;
      const response = await createSponsorPaymentLink({
        amount_cents: amountCents,
        currency: form.currency || "USD",
        service_name: form.paymentFor || "AfriMed consultation support",
        doctor_name: isPharmacySeller ? "" : doctorName,
        doctor_user_id: isPharmacySeller ? "" : doctorUserId,
        doctor_stripe_account_id: isPharmacySeller
          ? ""
          : String(flowState?.doctorStripeAccountId || "").trim(),
        seller_name: sellerDisplayName || (isPharmacySeller ? "" : doctorName),
        seller_user_id: isPharmacySeller ? sellerUserId : doctorUserId,
        seller_role: sellerRole,
        seller_stripe_account_id: isPharmacySeller
          ? String(
              flowState?.sellerStripeAccountId || flowState?.seller_stripe_account_id || ""
            ).trim()
          : "",
        prescription_id: String(
          flowState?.prescriptionId || flowMetadata?.prescription_id || ""
        ).trim(),
        pharmacy_owner_user_id: isPharmacySeller ? pharmacyOwnerUserId : "",
        patient_name: patientName,
        patient_user_id: auth?.user?.id || "",
        sponsor_name: form.sponsorName,
        sponsor_email: form.sponsorEmail,
        sponsor_phone: [form.sponsorDialCode, form.sponsorPhone].filter(Boolean).join(" "),
        sponsor_country: form.sponsorCountry,
        sponsor_relationship: form.relationship,
        success_url: resolvedSuccessUrl,
        cancel_url: resolvedCancelUrl,
        metadata: {
          sponsor_payment_lane: flowSource,
          payer_market: form.sponsorCountry || "",
          service_amount: fxHint.sourceAmount || basePaymentRef.current.amount || "",
          service_currency: fxHint.sourceCurrency || basePaymentRef.current.currency || "ZAR",
          sponsor_amount: form.amount || "",
          sponsor_currency: form.currency || "",
          fx_reference: fxHint.reference || "",
          fx_rate: fxHint.rate ? String(fxHint.rate) : "",
          ...(isPharmacySeller
            ? {
                doctor_name: doctorName,
                doctor_user_id: doctorUserId,
              }
            : {}),
          ...flowMetadata,
        },
      });

      const paymentLinkId = String(response?.id || "").trim();
      if (paymentLinkId && userId && flowSource) {
        savePendingSponsorPayment(userId, flowSource, {
          bookingKey: String(flowMetadata?.booking_key || "").trim(),
          paymentLinkId,
          paymentLinkUrl: String(response?.url || "").trim(),
          doctorUserId: doctorUserId,
          sellerUserId: isPharmacySeller ? sellerUserId : doctorUserId,
          patientUserId: String(auth?.user?.id || "").trim(),
          sponsorEmail: String(form.sponsorEmail || "").trim().toLowerCase(),
          sponsorName: String(form.sponsorName || "").trim(),
          serviceName: String(form.paymentFor || "").trim(),
          amountCents,
          currency: String(response?.currency || form.currency || "USD").trim(),
          recoveryHint: {
            ...flowMetadata,
            sponsor_payment_lane: flowSource,
            seller_role: sellerRole,
          },
        });
      }

      setLinkState({
        loading: false,
        url: String(response?.url || "").trim(),
        error: "",
        feeAmount: Number(response?.application_fee_amount || 0),
        doctorNetAmount: Number(response?.seller_net_amount || response?.doctor_net_amount || 0),
        amountCents: Number(response?.amount_cents || amountCents || 0),
        currency: String(response?.currency || form.currency || "USD").trim(),
        doctorDestinationAttached:
          response?.seller_destination_attached !== false &&
          response?.doctor_destination_attached !== false,
        paymentLinkId,
      });
    } catch (nextError) {
      if (userId && flowSource) {
        clearPendingSponsorPayment(userId, flowSource);
      }
      setLinkState({
        loading: false,
        url: "",
        error:
          String(nextError?.message || "").trim() ||
          t(
            "settings.preferences.transactions.sponsorCreateLinkError",
            "We could not create the sponsor payment link right now."
          ),
        feeAmount: 0,
        doctorNetAmount: 0,
        amountCents: 0,
        currency: "",
        doctorDestinationAttached: true,
        paymentLinkId: "",
      });
    }
  }

  const copyIcon = isDark
    ? "/icons/list-items/content_copy.svg"
    : "/icons/afrimed/content_copy.svg";

  return (
    <SettingsScreenShell>
      <TopBar
        title={t(
          "settings.preferences.transactions.sponsorSheetTitle",
          "Prepare sponsor payment"
        )}
        subtitle={t(
          "settings.preferences.transactions.sponsorSheetBody",
          "Use this to prepare a secure AfriMed™ payment request for family or sponsors across SADC and diaspora markets. Generate the secure payment link here first, then share or email it cleanly with the sponsor."
        )}
        onBack={() =>
          flowReturnTo
            ? navigate(flowReturnTo, { state: flowReturnState })
            : navigate("/settings/patient/preferences/transactions")
        }
        titleClassName="text-headline-small text-on-surface"
        titleWrapperClassName="mt-1 mb-5"
      />

      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-3">
          <MetricCard>
            <div className="text-label-large text-on-surface-var">
              {t("settings.preferences.transactions.sponsorSadc", "SADC markets")}
            </div>
            <MetricValue>{coverage.sadcCount}</MetricValue>
          </MetricCard>
          <MetricCard>
            <div className="text-label-large text-on-surface-var">
              {t("settings.preferences.transactions.sponsorDiaspora", "Diaspora markets")}
            </div>
            <MetricValue>{coverage.diasporaCount}</MetricValue>
          </MetricCard>
          <MetricCard>
            <div className="text-label-large text-on-surface-var">
              {t("settings.preferences.transactions.sponsorFx", "FX reference")}
            </div>
            <MetricValue>{coverage.referenceCurrencies.join(" / ")}</MetricValue>
          </MetricCard>
        </div>

        <div>
          <div className="text-title-large text-on-surface">
            {t("settings.preferences.transactions.sponsorContact", "Sponsor details")}
          </div>
          <div className="mt-3 grid gap-3">
            <TextField56
              label={t("settings.preferences.transactions.sponsorName", "Sponsor name")}
              value={form.sponsorName}
              onChange={(value) => patchForm({ sponsorName: value })}
              tone="surface"
              placeholder={t("settings.preferences.transactions.sponsorNamePlaceholder", "Full name")}
            />
            <TextField56
              label={t("settings.preferences.transactions.sponsorRelationship", "Relationship")}
              value={form.relationship}
              onChange={(value) => patchForm({ relationship: value })}
              tone="surface"
              placeholder={t(
                "settings.preferences.transactions.sponsorRelationshipPlaceholder",
                "Brother, sister, parent, guardian"
              )}
            />
            <InputCountrySADC
              label={t("settings.preferences.transactions.sponsorCountry", "Sponsor country")}
              value={form.sponsorCountry}
              onChange={(value) => {
                const market = getMarketByName(value);
                patchForm({
                  sponsorCountry: value,
                  sponsorDialCode: market?.dialCode || form.sponsorDialCode,
                });
              }}
              tone="surface"
              placeholderFlagIso={resolvedCountry?.iso || "za"}
            />
            <InputPhoneSADC
              value={form.sponsorPhone}
              onChange={(digits) => patchForm({ sponsorPhone: digits })}
              cc={form.sponsorDialCode}
              onChangeCc={(dialCode) => patchForm({ sponsorDialCode: dialCode })}
              tone="surface"
            />
            <TextField56
              label={t("settings.preferences.transactions.sponsorEmail", "Sponsor email")}
              value={form.sponsorEmail}
              onChange={(value) => patchForm({ sponsorEmail: value })}
              tone="surface"
              type="email"
              autoComplete="email"
              placeholder={t("settings.preferences.transactions.sponsorEmailPlaceholder", "name@example.com")}
            />
          </div>
        </div>

        <div>
          <div className="text-title-large text-on-surface">
            {t("settings.preferences.transactions.sponsorRequest", "Payment request")}
          </div>
          <div className="mt-3 grid gap-3">
            <TextField56
              label={t("settings.preferences.transactions.sponsorPaymentFor", "Payment for")}
              value={form.paymentFor}
              onChange={(value) => patchForm({ paymentFor: value })}
              tone="surface"
              placeholder={t(
                sellerRole === "pharmacy"
                  ? "settings.preferences.transactions.sponsorPaymentForPlaceholderPharmacy"
                  : "settings.preferences.transactions.sponsorPaymentForPlaceholderDoctor",
                sellerRole === "pharmacy" ? "Prescription fulfilment" : "Telemedicine consultation"
              )}
            />
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_120px]">
              <TextField56
                label={t("settings.preferences.transactions.sponsorAmount", "Sponsor amount")}
                value={form.amount}
                onChange={(value) =>
                  patchForm({ amount: String(value || "").replace(/[^\d.,]/g, "") })
                }
                tone="surface"
                inputMode="decimal"
                readOnly={sponsorPresentmentAuto}
                placeholder={t("settings.preferences.transactions.sponsorAmountPlaceholder", "50.00")}
              />
              <TextField56
                label={t("settings.preferences.transactions.sponsorCurrency", "Currency")}
                value={form.currency}
                onChange={(value) =>
                  patchForm({ currency: String(value || "").toUpperCase().slice(0, 3) })
                }
                tone="surface"
                readOnly={sponsorPresentmentAuto}
                placeholder={t("settings.preferences.transactions.sponsorCurrencyPlaceholderUsd", "USD")}
              />
            </div>
            <p className="text-body-medium text-on-surface-var">
              {sponsorPresentmentAuto
                ? fxHint.loading
                  ? t(
                      "settings.preferences.transactions.sponsorFxRefreshing",
                      "Refreshing fair FX sponsor amount…"
                    )
                  : t(
                      "settings.preferences.transactions.sponsorFxSentence",
                      "Service amount {amount} {currency} • {reference}.",
                      {
                        amount: fxHint.sourceAmount || basePaymentRef.current.amount || "0.00",
                        currency:
                          fxHint.sourceCurrency || basePaymentRef.current.currency || "ZAR",
                        reference:
                          [fxHint.reference, fxRateLabel].filter(Boolean).join(" • ") ||
                          t(
                            "settings.preferences.transactions.sponsorFxReference",
                            "Reference FX"
                          ),
                      }
                    )
                : t(
                    "settings.preferences.transactions.sponsorFxNote",
                    "Use a fair FX policy for diaspora payers. A sponsor in the UK or US should not be forced into a weak local-equivalence rate when the actual funding currency is stronger."
                  )}
            </p>
          </div>
        </div>

        <div>
          <div className="text-title-large text-on-surface">
            {t("settings.preferences.transactions.sponsorPreview", "Share preview")}
          </div>
          <SurfaceCard className="mt-3 p-0">
            <pre className="whitespace-pre-wrap break-words rounded-[12px] p-4 text-body-small text-on-surface-var">
              {sponsorBrief}
            </pre>
          </SurfaceCard>
        </div>

        <div>
          <div className="text-title-large text-on-surface">
            {t(
              "settings.preferences.transactions.sponsorLiveLinkTitle",
              "Secure sponsor payment link"
            )}
          </div>
          <div className="mt-2 text-body-medium text-on-surface-var">
            {t(
              "settings.preferences.transactions.sponsorLiveLinkBody",
              "Generate a secure AfriMed™ payment link that can be shared with family or sponsors. This makes the sponsor journey cleaner than asking them to manually coordinate payment."
            )}
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Button
              type="button"
              variant="default"
              onClick={handleGenerateLink}
              disabled={!canGenerateLink || linkState.loading}
              className="w-full sm:w-auto"
            >
              {linkState.loading
                ? t("common.loading", "Loading...")
                : t(
                    "settings.preferences.transactions.sponsorGenerateLink",
                    "Generate payment link"
                  )}
            </Button>
            <Button
              type="button"
              variant="tonal"
              onClick={handleCopyBrief}
              disabled={!canShare}
              className="w-full sm:w-auto"
            >
              <span className="inline-flex items-center gap-2">
                <img src={copyIcon} alt="" aria-hidden="true" className="h-4 w-4 object-contain" />
                <span>
                  {copied
                    ? t("settings.preferences.transactions.sponsorCopied", "Brief copied")
                    : t("settings.preferences.transactions.sponsorCopy", "Copy sponsor brief")}
                </span>
              </span>
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={handleOpenEmail}
              disabled={!canOpenEmailDraft}
              className="w-full sm:w-auto"
            >
              {emailState.sending
                ? t("common.loading", "Loading...")
                : t(
                    "settings.preferences.transactions.sponsorEmailActionServer",
                    "Send email request",
                  )}
            </Button>
          </div>

          {linkState.error ? (
            <div className="mt-4 rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
              {linkState.error}
            </div>
          ) : null}

          {!linkState.url ? (
            <SurfaceCard className="mt-4">
              <div className="text-body-small text-on-surface-var">
                {t(
                  "settings.preferences.transactions.sponsorLinkRequired",
                  "Generate the secure payment link before sending the email so the sponsor receives a real checkout action."
                )}
              </div>
            </SurfaceCard>
          ) : null}

          {emailState.error ? (
            <div className="mt-4 rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
              {emailState.error}
            </div>
          ) : null}

          {emailState.success ? (
            <div className="mt-4 rounded-[12px] bg-[var(--state-positive-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-positive))]">
              {emailState.success}
            </div>
          ) : null}

          {linkState.url ? (
            <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
              <SurfaceCard>
                <div className="text-label-large text-on-surface-var">
                  {t("settings.preferences.transactions.sponsorGeneratedUrl", "Generated link")}
                </div>
                <a
                  href={linkState.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 block break-all text-body-medium text-primary underline underline-offset-4"
                >
                  {linkState.url}
                </a>
              </SurfaceCard>
              <SurfaceCard>
                <div className="text-label-large text-on-surface-var">
                  {t(
                    "settings.preferences.transactions.sponsorPlatformSummary",
                    "Settlement preview"
                  )}
                </div>
                <div className="mt-2 space-y-1 text-body-medium text-on-surface">
                  {linkState.currency
                    ? (
                        <>
                          <div>
                            {t(
                              "settings.preferences.transactions.sponsorSettlementCost",
                              "Cost: {amount} {currency}",
                              {
                                amount: (linkState.amountCents / 100).toFixed(2),
                                currency: linkState.currency,
                              }
                            )}
                          </div>
                          <div>
                            {t(
                              "settings.preferences.transactions.sponsorSettlementFee",
                              "AfriMed™ fee: {amount} {currency}",
                              {
                                amount: (linkState.feeAmount / 100).toFixed(2),
                                currency: linkState.currency,
                              }
                            )}
                          </div>
                          <div>
                            {t(
                              "settings.preferences.transactions.sponsorSettlementNet",
                              "Balance to {seller} payout account: {amount} {currency}",
                              {
                                amount: (linkState.doctorNetAmount / 100).toFixed(2),
                                currency: linkState.currency,
                                seller: sellerLabel,
                              }
                            )}
                          </div>
                        </>
                      )
                    : "—"}
                </div>
                {!linkState.currency || !linkState.doctorDestinationAttached ? (
                  <div className="mt-1 text-body-small text-on-surface-var">
                    {linkState.currency
                      ? t(
                          "settings.preferences.transactions.sponsorSettlementMissingDestination",
                          "This link is active, but the connected {seller} payout destination is not attached yet. Reconnect Stripe before using it live.",
                          { seller: sellerLabel }
                        )
                      : t(
                          "settings.preferences.transactions.sponsorPlatformSummaryEmpty",
                          "Settlement preview appears here when the payout destination is attached."
                        )}
                  </div>
                ) : null}
              </SurfaceCard>
            </div>
          ) : null}
        </div>
      </div>
    </SettingsScreenShell>
  );
}
